import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  GraduationCap,
  Loader2,
  AlertCircle,
  ArrowLeft,
  KeyRound,
  CheckCircle2,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

// Attivazione account creato dall'admin: l'utente arriva qui dal link dell'email
// d'invito e sceglie la propria password. Gemello di ResetPassword, ma sul flusso
// /auth/invite e sende link di auto-richiesta (l'invito lo emette solo l'admin).
export default function SetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  // checking | valid | invalid | done
  const [status, setStatus] = useState("checking");
  const [emailMasked, setEmailMasked] = useState("");
  const [form, setForm] = useState({ new_password: "", confirm_password: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/auth/invite/${encodeURIComponent(token)}`);
        if (data.valid) {
          setEmailMasked(data.email_masked || "");
          setStatus("valid");
        } else {
          setStatus("invalid");
        }
      } catch {
        setStatus("invalid");
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password.length < 8) {
      setError("La password deve avere almeno 8 caratteri.");
      return;
    }
    if (form.new_password !== form.confirm_password) {
      setError("Le password non coincidono.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/invite", { token, ...form });
      setStatus("done");
      setTimeout(() => navigate("/login", { replace: true }), 3000);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="set-password-page"
      className="min-h-screen bg-brand-bg brand-grid-bg flex flex-col"
    >
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <Link
          to="/login"
          data-testid="setpw-back-login"
          className="inline-flex items-center gap-1.5 text-sm text-brand-blue hover:underline underline-offset-4"
        >
          <ArrowLeft size={16} /> Torna all’accesso
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 pb-16">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2.5 justify-center mb-8">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-sm bg-brand-blue text-white">
              <GraduationCap size={22} strokeWidth={2} />
            </span>
            <div className="leading-tight">
              <div className="wordmark text-brand-blue text-xl">Buro Academy</div>
              <div className="text-xs text-brand-muted -mt-0.5">Burocratica Servizi</div>
            </div>
          </div>

          <div className="bg-white border border-brand-line rounded-sm shadow-sm p-8">
            <div className="gold-rule" />

            {status === "checking" && (
              <div
                data-testid="setpw-checking"
                className="flex items-center gap-2 text-sm text-brand-muted py-4"
              >
                <Loader2 size={18} className="animate-spin" /> Verifica del link in corso…
              </div>
            )}

            {status === "invalid" && (
              <div data-testid="setpw-invalid">
                <h1 className="text-2xl text-brand-ink mb-2">Link non valido</h1>
                <p className="text-sm text-brand-muted mb-6">
                  Il link d’invito è scaduto, è già stato utilizzato oppure non è
                  corretto. Contatta l’amministratore per ricevere un nuovo invito.
                </p>
                <Link
                  to="/login"
                  data-testid="setpw-goto-login"
                  className="btn-primary w-full inline-flex items-center justify-center gap-2"
                >
                  Vai all’accesso
                </Link>
              </div>
            )}

            {status === "done" && (
              <div data-testid="setpw-done">
                <div className="flex items-center gap-2 text-brand-blue mb-3">
                  <CheckCircle2 size={22} />
                  <h1 className="text-2xl text-brand-ink">Account attivato</h1>
                </div>
                <p className="text-sm text-brand-muted mb-6">
                  La tua password è impostata. Verrai reindirizzato alla pagina di
                  accesso tra qualche istante.
                </p>
                <Link
                  to="/login"
                  data-testid="setpw-login-link"
                  className="btn-primary w-full inline-flex items-center justify-center gap-2"
                >
                  Vai all’accesso
                </Link>
              </div>
            )}

            {status === "valid" && (
              <>
                <h1 className="text-2xl text-brand-ink mb-1">Imposta la tua password</h1>
                <p className="text-sm text-brand-muted mb-6">
                  Benvenuto su Buro Academy.{" "}
                  {emailMasked ? (
                    <>
                      Account: <span className="font-semibold text-brand-ink">{emailMasked}</span>.{" "}
                    </>
                  ) : null}
                  Scegli una password di almeno 8 caratteri per completare
                  l’attivazione dell’account.
                </p>

                <form onSubmit={handleSubmit} className="space-y-5" data-testid="setpw-form">
                  <div>
                    <Label htmlFor="new_password" className="text-sm text-brand-ink">
                      Password
                    </Label>
                    <Input
                      id="new_password"
                      data-testid="setpw-new-password"
                      type="password"
                      autoComplete="new-password"
                      minLength={8}
                      value={form.new_password}
                      onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                      placeholder="••••••••"
                      className="mt-1.5 h-11 rounded-sm border-brand-line focus-visible:ring-2 focus-visible:ring-brand-blue"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="confirm_password" className="text-sm text-brand-ink">
                      Conferma password
                    </Label>
                    <Input
                      id="confirm_password"
                      data-testid="setpw-confirm-password"
                      type="password"
                      autoComplete="new-password"
                      minLength={8}
                      value={form.confirm_password}
                      onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                      placeholder="••••••••"
                      className="mt-1.5 h-11 rounded-sm border-brand-line focus-visible:ring-2 focus-visible:ring-brand-blue"
                      required
                    />
                  </div>

                  {error && (
                    <div
                      data-testid="setpw-error"
                      role="alert"
                      className="flex items-start gap-2 rounded-sm border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                    >
                      <AlertCircle size={16} className="mt-0.5 shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    data-testid="setpw-submit-btn"
                    disabled={saving}
                    className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <Loader2 size={18} className="animate-spin" /> Salvataggio…
                      </>
                    ) : (
                      <>
                        <KeyRound size={16} /> Attiva l’account
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
